import { Checkbox, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import type { DateWindow } from "../../../../utils/milestone-effective-schedule.ts"

const { Text } = Typography

export interface ScheduleImpactEntry {
  id: string
  name: string
  window: DateWindow
  previousWindow: DateWindow | null
  /** No stored Planned Start/End: its window exists only because of a
   * dependency, so saving it records nothing the user chose by hand. */
  dependencyDriven: boolean
}

/** Which of the shifted dependents the user asked to save. Read by the
 * confirmation's OK handler, which antd calls outside React. */
export interface ScheduleImpactChoice {
  saveDependencyDriven: boolean
  saveOwnDates: boolean
}

export const defaultScheduleImpactChoice = (): ScheduleImpactChoice => ({
  saveDependencyDriven: true,
  saveOwnDates: false
})

export const idsToSave = (
  entries: ScheduleImpactEntry[],
  choice: ScheduleImpactChoice
) =>
  entries
    .filter((entry) =>
      entry.dependencyDriven ? choice.saveDependencyDriven : choice.saveOwnDates
    )
    .map((entry) => entry.id)

export function MilestoneScheduleImpactContent({
  lede,
  entries,
  choice
}: {
  lede: string
  entries: ScheduleImpactEntry[]
  /** Mutated in place, so the caller's OK handler reads the latest ticks. */
  choice: ScheduleImpactChoice
}) {
  const { t } = useTranslation()
  const [saveDependencyDriven, setSaveDependencyDriven] = useState(
    choice.saveDependencyDriven
  )
  const [saveOwnDates, setSaveOwnDates] = useState(choice.saveOwnDates)

  const dependencyDrivenCount = entries.filter((e) => e.dependencyDriven).length
  const ownDatesCount = entries.length - dependencyDrivenCount

  return (
    <>
      <p>{lede}</p>
      <ul className="list-disc pl-4">
        {entries.map(({ id, name, window, previousWindow, dependencyDriven }) => (
          <li key={id}>
            {name}:{" "}
            {previousWindow && (
              <>
                {previousWindow.start.format("MMM D")} –{" "}
                {previousWindow.end.format("MMM D")} →{" "}
              </>
            )}
            {window.start.format("MMM D")} – {window.end.format("MMM D")}
            {dependencyDriven ? (
              <Text type="secondary">
                {" "}
                {t("milestoneScheduleImpact.noDatesOfItsOwn")}
              </Text>
            ) : null}
          </li>
        ))}
      </ul>
      {ownDatesCount ? (
        <p>{t("milestoneScheduleImpact.shownShifted")}</p>
      ) : null}
      <div className="flex flex-col gap-1">
        {dependencyDrivenCount ? (
          <Checkbox
            checked={saveDependencyDriven}
            onChange={(e) => {
              choice.saveDependencyDriven = e.target.checked
              setSaveDependencyDriven(e.target.checked)
            }}
          >
            {t("milestoneScheduleImpact.saveDependencyDriven", {
              count: dependencyDrivenCount
            })}
          </Checkbox>
        ) : null}
        {ownDatesCount ? (
          <Checkbox
            checked={saveOwnDates}
            onChange={(e) => {
              choice.saveOwnDates = e.target.checked
              setSaveOwnDates(e.target.checked)
            }}
          >
            {t("milestoneScheduleImpact.saveOwnDates", {
              count: ownDatesCount
            })}
          </Checkbox>
        ) : null}
      </div>
    </>
  )
}
